import React, { useCallback, useMemo, useState } from 'react';

import classNames from 'classnames';
import { useMutation, useQuery, useQueryCache } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import {
  createResolutionStrategy,
  createResolutionStrategyTag,
  deleteResolutionStrategyTag,
  fetchResolutionStrategies,
  reorderResolutionStrategyTags,
  updateResolutionStrategyTag,
} from '../api';
import type {
  CreateResolutionStrategyPayload,
  ReorderTagItem,
  ResolutionStrategy,
  ResolutionStrategyTag,
} from '../api';

const DEFAULT_STRATEGY_TAG = 'Otros';
const DEFAULT_RESOLUTION_TIME_SECONDS = 60;

interface ResolutionStrategyTabProps {
  accountId: number;
}

interface TagWithDepth extends ResolutionStrategyTag {
  depth: number;
  isLeaf: boolean;
  pathLabel: string;
  /**
   * Per-ancestor "is this ancestor the last child of its parent?" — used by
   * the visual tree connectors so we know which guides should bend (└) vs
   * continue down (│). Length === depth.
   */
  ancestorIsLastChild: boolean[];
  isLastChild: boolean;
}

type DropPosition = 'before' | 'after' | 'inside';

interface DropTarget {
  id: number;
  position: DropPosition;
}

interface RootGroup {
  root: TagWithDepth;
  subtree: TagWithDepth[];
}

/** Pixels of indent per depth level. Wider than before so the hierarchy reads clearly. */
const INDENT_PX = 28;
/** Left padding for the row's first content (before the indent guides). */
const ROW_LEFT_PX = 12;

const buildPath = (
  tag: ResolutionStrategyTag,
  byId: Map<number, ResolutionStrategyTag>
): string => {
  const segments: string[] = [];
  let cursor: ResolutionStrategyTag | undefined = tag;
  let safety = 0;
  while (cursor && safety < 50) {
    segments.unshift(cursor.name);
    cursor = cursor.parent_id == null ? undefined : byId.get(cursor.parent_id);
    safety += 1;
  }
  return segments.join(' › ');
};

const compareSiblings = (a: ResolutionStrategyTag, b: ResolutionStrategyTag): number => {
  const ao = a.sort_order ?? 0;
  const bo = b.sort_order ?? 0;
  if (ao !== bo) return ao - bo;
  return a.id - b.id;
};

/**
 * Flatten a tag tree into pre-order rows annotated with depth, leaf-ness,
 * the breadcrumb path, and per-ancestor "isLastChild" flags. The flags let
 * the visual tree connectors draw the canonical ├── / └── shapes.
 *
 * Siblings are ordered by `(sort_order, id)` so the UI mirrors the
 * server-side ordering used during persistence.
 */
const flattenTree = (tags: ResolutionStrategyTag[]): TagWithDepth[] => {
  const byId = new Map<number, ResolutionStrategyTag>();
  tags.forEach((t) => byId.set(t.id, t));

  const childrenByParent = new Map<number | null, ResolutionStrategyTag[]>();
  tags.forEach((t) => {
    const key = t.parent_id ?? null;
    const arr = childrenByParent.get(key) ?? [];
    arr.push(t);
    childrenByParent.set(key, arr);
  });
  childrenByParent.forEach((arr) => arr.sort(compareSiblings));

  const out: TagWithDepth[] = [];
  const visit = (
    node: ResolutionStrategyTag,
    depth: number,
    ancestorIsLastChild: boolean[],
    isLastChild: boolean
  ): void => {
    const kids = childrenByParent.get(node.id) ?? [];
    out.push({
      ...node,
      depth,
      isLeaf: kids.length === 0,
      pathLabel: buildPath(node, byId),
      ancestorIsLastChild,
      isLastChild,
    });
    kids.forEach((k, idx) =>
      visit(k, depth + 1, [...ancestorIsLastChild, isLastChild], idx === kids.length - 1)
    );
  };
  const roots = childrenByParent.get(null) ?? [];
  roots.forEach((root, idx) => visit(root, 0, [], idx === roots.length - 1));
  return out;
};

/**
 * Split the flat pre-order list into one group per root subtree. Pre-order
 * traversal guarantees a root is followed by all its descendants, so we can
 * partition with a single pass.
 */
const groupByRoot = (rows: TagWithDepth[]): RootGroup[] => {
  const groups: RootGroup[] = [];
  let current: RootGroup | null = null;
  rows.forEach((row) => {
    if (row.depth === 0) {
      current = { root: row, subtree: [row] };
      groups.push(current);
    } else if (current) {
      current.subtree.push(row);
    }
  });
  return groups;
};

/** True if `candidate` is `tag` itself or one of its descendants. */
const isDescendantOf = (
  candidateId: number,
  tagId: number,
  tags: ResolutionStrategyTag[]
): boolean => {
  if (candidateId === tagId) return true;
  const childrenByParent = new Map<number, ResolutionStrategyTag[]>();
  tags.forEach((t) => {
    if (t.parent_id != null) {
      const arr = childrenByParent.get(t.parent_id) ?? [];
      arr.push(t);
      childrenByParent.set(t.parent_id, arr);
    }
  });
  const stack = [tagId];
  while (stack.length > 0) {
    const current = stack.pop() as number;
    if (current === candidateId) return true;
    const kids = childrenByParent.get(current) ?? [];
    kids.forEach((k) => stack.push(k.id));
  }
  return false;
};

/**
 * Apply a drag-drop transition (`dragged` → `target` at `position`) and return
 * the resulting flat list with normalized `parent_id` / `sort_order` for every
 * tag. Siblings under each parent are renumbered 0..N-1 to keep ordering stable.
 *
 * Returns `null` if the move would be invalid (target inside dragged subtree
 * or a no-op).
 */
const applyDrop = (
  tags: ResolutionStrategyTag[],
  draggedId: number,
  target: DropTarget
): ReorderTagItem[] | null => {
  if (draggedId === target.id) return null;
  if (isDescendantOf(target.id, draggedId, tags)) return null;

  const targetTag = tags.find((t) => t.id === target.id);
  const draggedTag = tags.find((t) => t.id === draggedId);
  if (!targetTag || !draggedTag) return null;

  const newParentId = target.position === 'inside' ? target.id : targetTag.parent_id ?? null;

  // Group siblings by parent (excluding the dragged tag from its old position).
  const childrenByParent = new Map<number | null, ResolutionStrategyTag[]>();
  tags.forEach((t) => {
    if (t.id === draggedId) return;
    const key = t.parent_id ?? null;
    const arr = childrenByParent.get(key) ?? [];
    arr.push(t);
    childrenByParent.set(key, arr);
  });
  childrenByParent.forEach((arr) => arr.sort(compareSiblings));

  // Insert the dragged tag at the right index under newParentId.
  const newSiblings = [...(childrenByParent.get(newParentId) ?? [])];
  let insertIndex: number;
  if (target.position === 'inside') {
    insertIndex = newSiblings.length;
  } else {
    const targetIdx = newSiblings.findIndex((t) => t.id === target.id);
    insertIndex = target.position === 'before' ? targetIdx : targetIdx + 1;
  }
  newSiblings.splice(insertIndex, 0, { ...draggedTag, parent_id: newParentId });
  childrenByParent.set(newParentId, newSiblings);

  // Renumber every group so sort_order is dense.
  const result: ReorderTagItem[] = [];
  childrenByParent.forEach((siblings, parentId) => {
    siblings.forEach((tag, idx) => {
      result.push({ id: tag.id, parent_id: parentId, sort_order: idx });
    });
  });
  return result;
};

/**
 * Visual tree connectors drawn left of each row. We render one vertical
 * "rail" per ancestor depth (or empty space if that ancestor was the last
 * child of its parent), and an `└──` / `├──` glyph for the row's own depth.
 */
const TreeIndentGuides: React.FC<{ row: TagWithDepth }> = ({ row }) => {
  if (row.depth === 0) return null;
  const cells: React.ReactNode[] = [];
  for (let i = 0; i < row.depth - 1; i += 1) {
    const ancestorIsLast = row.ancestorIsLastChild[i + 1] ?? false;
    cells.push(
      <span
        key={`rail-${i}`}
        aria-hidden="true"
        className="absolute top-0 bottom-0 w-px pointer-events-none"
        style={{
          left: ROW_LEFT_PX + i * INDENT_PX + INDENT_PX / 2,
          background: ancestorIsLast ? 'transparent' : 'var(--white-3-0, #E5E7EB)',
        }}
      />
    );
  }
  // The connector for this row's own depth — vertical guide on the left half
  // of the cell, plus a horizontal stub. The vertical half stops at center
  // when this is the last child (└), otherwise continues down (├).
  const cellLeft = ROW_LEFT_PX + (row.depth - 1) * INDENT_PX + INDENT_PX / 2;
  cells.push(
    <span key="vertical-self" aria-hidden="true">
      <span
        className="absolute left-0 w-px pointer-events-none"
        style={{
          top: 0,
          bottom: row.isLastChild ? '50%' : 0,
          left: cellLeft,
          background: 'var(--white-3-0, #E5E7EB)',
        }}
      />
      <span
        className="absolute h-px pointer-events-none"
        style={{
          top: '50%',
          left: cellLeft,
          width: INDENT_PX / 2,
          background: 'var(--white-3-0, #E5E7EB)',
        }}
      />
    </span>
  );
  return <>{cells}</>;
};

interface TagRowProps {
  row: TagWithDepth;
  parentOptions: TagWithDepth[];
  isEditing: boolean;
  isCollapsed: boolean;
  hasChildren: boolean;
  childCount: number;
  onToggleCollapse: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSave: (payload: { name: string; parentId: number | null }) => void;
  onDelete: () => void;
  onAddChild: () => void;
  isSaving: boolean;
  isDeleting: boolean;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent<HTMLLIElement>) => void;
  onDragLeave: () => void;
  onDrop: () => void;
  onDragEnd: () => void;
  isDragging: boolean;
  isDragInvalid: boolean;
  dropPosition: DropPosition | null;
}

const TagRow: React.FC<TagRowProps> = ({
  row,
  parentOptions,
  isEditing,
  isCollapsed,
  hasChildren,
  childCount,
  onToggleCollapse,
  onStartEdit,
  onCancelEdit,
  onSave,
  onDelete,
  onAddChild,
  isSaving,
  isDeleting,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  isDragging,
  isDragInvalid,
  dropPosition,
}) => {
  const [name, setName] = useState(row.name);
  const [parentId, setParentId] = useState<number | null>(row.parent_id ?? null);

  React.useEffect(() => {
    if (isEditing) {
      setName(row.name);
      setParentId(row.parent_id ?? null);
    }
  }, [isEditing, row.name, row.parent_id]);

  const isRoot = row.depth === 0;
  // Root rows are rendered as a section header (slightly taller, gray-ish
  // background); all rows are still draggable and droppable so the user can
  // reorder roots between cards or pull a sub-tag back to the root level.
  const paddingLeft = ROW_LEFT_PX + row.depth * INDENT_PX;

  return (
    <li
      className={classNames(
        'group relative flex items-center gap-2 border-b border-white-2.0 px-3 last:border-b-0',
        isRoot ? 'bg-white-1.0 py-3' : 'py-2',
        isEditing ? 'bg-blue-50' : 'hover:bg-blue-50/40',
        isDragging && 'opacity-40',
        dropPosition === 'inside' && !isDragInvalid && 'bg-blue-50',
        dropPosition && isDragInvalid && 'bg-red-50'
      )}
      style={{ paddingLeft }}
      draggable={!isEditing}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
    >
      {!isEditing && <TreeIndentGuides row={row} />}
      {dropPosition === 'before' && (
        <span
          className={classNames(
            'pointer-events-none absolute left-0 right-0 top-0 h-0.5',
            isDragInvalid ? 'bg-red-500' : 'bg-blue-500'
          )}
          aria-hidden="true"
        />
      )}
      {dropPosition === 'after' && (
        <span
          className={classNames(
            'pointer-events-none absolute left-0 right-0 bottom-0 h-0.5',
            isDragInvalid ? 'bg-red-500' : 'bg-blue-500'
          )}
          aria-hidden="true"
        />
      )}
      {!isEditing && (
        <span
          className="cursor-grab text-gray-2.0 hover:text-gray-3.0 active:cursor-grabbing"
          title="Arrastra para reordenar"
          aria-hidden="true"
        >
          <Icon name={OrbitIcons.GripVertical} size={14} />
        </span>
      )}
      {!isEditing && (
        <button
          type="button"
          onClick={hasChildren ? onToggleCollapse : undefined}
          disabled={!hasChildren}
          className={classNames(
            'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded text-gray-3.0',
            hasChildren ? 'hover:bg-white-2.0' : 'cursor-default opacity-0 pointer-events-none'
          )}
          aria-label={
            hasChildren ? (isCollapsed ? 'Expandir sub-categorías' : 'Colapsar sub-categorías') : ''
          }
          aria-expanded={hasChildren ? !isCollapsed : undefined}
        >
          {hasChildren && (
            <Icon name={isCollapsed ? OrbitIcons.ChevronRight : OrbitIcons.ChevronDown} size={14} />
          )}
        </button>
      )}
      {!isEditing ? (
        <>
          <Icon
            name={row.isLeaf ? OrbitIcons.Hash : OrbitIcons.Workflow}
            size={isRoot ? 16 : 14}
            className={row.isLeaf ? 'text-gray-2.0' : 'text-blue-500'}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span
                className={classNames(
                  'truncate text-black-1.0',
                  isRoot ? 'text-base font-semibold' : 'text-sm font-medium'
                )}
              >
                {row.name}
              </span>
              {!row.isLeaf && (
                <span
                  className={classNames(
                    'rounded px-1.5 py-0.5 text-[10px] font-bold uppercase',
                    isRoot ? 'bg-blue-100 text-blue-700' : 'bg-white-2.0 text-gray-3.0'
                  )}
                >
                  {childCount} sub-categoría{childCount === 1 ? '' : 's'}
                </span>
              )}
            </div>
            {!isRoot && row.pathLabel !== row.name && (
              <div className="truncate text-[11px] text-gray-2.0">{row.pathLabel}</div>
            )}
          </div>
          <button
            type="button"
            className="hover:bg-blue-100 group-hover:opacity-100 px-2 py-1 text-xs font-medium text-blue-700 rounded opacity-0"
            onClick={onAddChild}
            title="Agregar sub-categoría"
          >
            <Icon name={OrbitIcons.PlusCircle} size={14} />
          </button>
          <button
            type="button"
            className="rounded px-2 py-1 text-xs font-medium text-gray-3.0 opacity-0 hover:bg-white-2.0 group-hover:opacity-100"
            onClick={onStartEdit}
            title="Editar"
          >
            <Icon name={OrbitIcons.Pencil} size={14} />
          </button>
          <button
            type="button"
            className="hover:bg-red-50 disabled:opacity-50 group-hover:opacity-100 px-2 py-1 text-xs font-medium text-red-600 rounded opacity-0"
            onClick={onDelete}
            disabled={!row.isLeaf || isDeleting}
            title={row.isLeaf ? 'Eliminar' : 'No se puede borrar: tiene sub-categorías'}
          >
            <Icon name={OrbitIcons.Trash} size={14} />
          </button>
        </>
      ) : (
        <div className="flex flex-wrap items-center flex-1 gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="min-w-[140px] flex-1 rounded border border-white-3.0 px-2 py-1 text-sm"
            placeholder="Nombre"
          />
          <select
            value={parentId ?? ''}
            onChange={(e) => setParentId(e.target.value === '' ? null : Number(e.target.value))}
            className="rounded border border-white-3.0 px-2 py-1 text-sm"
          >
            <option value="">— Sin categoría padre —</option>
            {parentOptions.map((opt) => (
              <option key={opt.id} value={opt.id}>
                {'\u00A0\u00A0'.repeat(opt.depth)}
                {opt.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white-0.0 disabled:opacity-50"
            onClick={() => onSave({ name: name.trim(), parentId })}
            disabled={isSaving || name.trim() === ''}
          >
            Guardar
          </button>
          <button
            type="button"
            className="rounded border border-white-3.0 px-3 py-1 text-xs font-medium text-gray-3.0"
            onClick={onCancelEdit}
            disabled={isSaving}
          >
            Cancelar
          </button>
        </div>
      )}
    </li>
  );
};

interface CreateTagFormProps {
  parentOptions: TagWithDepth[];
  defaultParentId: number | null;
  onSubmit: (payload: { name: string; parentId: number | null }) => void;
  isSaving: boolean;
}

const CreateTagForm: React.FC<CreateTagFormProps> = ({
  parentOptions,
  defaultParentId,
  onSubmit,
  isSaving,
}) => {
  const [name, setName] = useState('');
  const [parentId, setParentId] = useState<number | null>(defaultParentId);

  React.useEffect(() => {
    setParentId(defaultParentId);
  }, [defaultParentId]);

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border border-dashed border-white-3.0 bg-white-0.0 p-3">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="min-w-[140px] flex-1 rounded border border-white-3.0 px-2 py-1 text-sm"
        placeholder="Nombre de la nueva categoría / sub-categoría"
      />
      <select
        value={parentId ?? ''}
        onChange={(e) => setParentId(e.target.value === '' ? null : Number(e.target.value))}
        className="rounded border border-white-3.0 px-2 py-1 text-sm"
      >
        <option value="">— Sin categoría padre —</option>
        {parentOptions.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {'\u00A0\u00A0'.repeat(opt.depth)}
            {opt.name}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white-0.0 disabled:opacity-50"
        onClick={() => {
          onSubmit({ name: name.trim(), parentId });
          setName('');
        }}
        disabled={isSaving || name.trim() === ''}
      >
        <Icon name={OrbitIcons.PlusCircle} size={14} className="inline mr-1" />
        Agregar
      </button>
    </div>
  );
};

export const ResolutionStrategyTab: React.FC<ResolutionStrategyTabProps> = ({ accountId }) => {
  const queryCache = useQueryCache();
  const [selectedStrategyId, setSelectedStrategyId] = useState<number | null>(null);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [createDefaultParentId, setCreateDefaultParentId] = useState<number | null>(null);
  const [draggingTagId, setDraggingTagId] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
  const [collapsedTagIds, setCollapsedTagIds] = useState<Set<number>>(new Set());
  const [showCreateStrategy, setShowCreateStrategy] = useState(false);
  const [newStrategyName, setNewStrategyName] = useState('');
  const [newStrategyTag, setNewStrategyTag] = useState(DEFAULT_STRATEGY_TAG);
  const [newStrategyResolutionTime, setNewStrategyResolutionTime] = useState(
    DEFAULT_RESOLUTION_TIME_SECONDS
  );

  const resetCreateStrategyForm = useCallback(() => {
    setShowCreateStrategy(false);
    setNewStrategyName('');
    setNewStrategyTag(DEFAULT_STRATEGY_TAG);
    setNewStrategyResolutionTime(DEFAULT_RESOLUTION_TIME_SECONDS);
  }, []);

  const isNewStrategyValid = newStrategyName.trim().length > 0 && newStrategyResolutionTime > 0;

  const {
    data: strategies = [],
    isLoading: isLoadingStrategies,
    error: strategiesError,
  } = useQuery<ResolutionStrategy[], Error>(
    ['resolution-strategies', accountId],
    () => fetchResolutionStrategies(accountId),
    {
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.length > 0 && selectedStrategyId === null) {
          setSelectedStrategyId(data[0].id);
        }
      },
    }
  );

  const selectedStrategy = useMemo(
    () => strategies.find((s) => s.id === selectedStrategyId) ?? null,
    [strategies, selectedStrategyId]
  );

  const tagsForStrategy = useMemo(() => selectedStrategy?.tags ?? [], [selectedStrategy]);

  const flattenedTags = useMemo(() => flattenTree(tagsForStrategy), [tagsForStrategy]);

  // Per-tag direct child count, used for the "N sub-categorías" badge so we
  // don't have to recompute children inside every row render.
  const childCountById = useMemo(() => {
    const counts = new Map<number, number>();
    tagsForStrategy.forEach((t) => {
      if (t.parent_id != null) {
        counts.set(t.parent_id, (counts.get(t.parent_id) ?? 0) + 1);
      }
    });
    return counts;
  }, [tagsForStrategy]);

  const rootGroups = useMemo(() => groupByRoot(flattenedTags), [flattenedTags]);

  // Returns true if any ancestor of `row` is currently collapsed → row is hidden.
  const isRowHidden = (row: TagWithDepth): boolean => {
    if (collapsedTagIds.size === 0) return false;
    let cursorParent = row.parent_id ?? null;
    const byId = new Map(tagsForStrategy.map((t) => [t.id, t]));
    while (cursorParent != null) {
      if (collapsedTagIds.has(cursorParent)) return true;
      const next = byId.get(cursorParent);
      if (!next) break;
      cursorParent = next.parent_id ?? null;
    }
    return false;
  };

  const toggleCollapse = (tagId: number): void => {
    setCollapsedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) next.delete(tagId);
      else next.add(tagId);
      return next;
    });
  };

  const invalidate = (): void => {
    queryCache.invalidateQueries(['resolution-strategies', accountId]);
  };

  const [createStrategy, { isLoading: isCreatingStrategy }] = useMutation(
    (payload: CreateResolutionStrategyPayload) => createResolutionStrategy(accountId, payload),
    {
      onSuccess: (data) => {
        Toast.success('Estrategia creada');
        resetCreateStrategyForm();
        invalidate();
        setSelectedStrategyId(data.id);
      },
      onError: () => {
        Toast.error('No se pudo crear la estrategia');
      },
    }
  );

  const [createTag, { isLoading: isCreating }] = useMutation(createResolutionStrategyTag, {
    onSuccess: () => {
      Toast.success('Categoría creada');
      invalidate();
    },
    onError: () => {
      Toast.error('No se pudo crear la categoría');
    },
  });

  const [updateTag, { isLoading: isUpdating }] = useMutation(
    ({
      tagId,
      payload,
    }: {
      tagId: number;
      payload: Parameters<typeof updateResolutionStrategyTag>[1];
    }) => updateResolutionStrategyTag(tagId, payload),
    {
      onSuccess: () => {
        Toast.success('Categoría actualizada');
        setEditingTagId(null);
        invalidate();
      },
      onError: () => {
        Toast.error('No se pudo actualizar la categoría');
      },
    }
  );

  const [removeTag, { isLoading: isDeleting }] = useMutation(deleteResolutionStrategyTag, {
    onSuccess: () => {
      Toast.success('Categoría eliminada');
      invalidate();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } };
      const detail = axiosErr?.response?.data?.detail ?? 'No se pudo eliminar la categoría';
      Toast.error(typeof detail === 'string' ? detail : 'No se pudo eliminar la categoría');
    },
  });

  const [reorderTags] = useMutation(reorderResolutionStrategyTags, {
    onSuccess: () => {
      Toast.success('Orden actualizado');
      invalidate();
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: { detail?: unknown } } };
      const detail = axiosErr?.response?.data?.detail ?? 'No se pudo guardar el nuevo orden';
      Toast.error(typeof detail === 'string' ? detail : 'No se pudo guardar el nuevo orden');
      invalidate();
    },
  });

  const handleCreate = (payload: { name: string; parentId: number | null }): void => {
    if (!selectedStrategyId) return;
    createTag({
      strategy_id: selectedStrategyId,
      name: payload.name,
      parent_id: payload.parentId,
    });
  };

  const handleUpdate = (
    tag: ResolutionStrategyTag,
    payload: { name: string; parentId: number | null }
  ): void => {
    const body: Parameters<typeof updateResolutionStrategyTag>[1] = {};
    if (payload.name !== tag.name) body.name = payload.name;
    if ((tag.parent_id ?? null) !== payload.parentId) {
      if (payload.parentId == null) {
        body.clear_parent = true;
      } else {
        body.parent_id = payload.parentId;
      }
    }
    if (Object.keys(body).length === 0) {
      setEditingTagId(null);
      return;
    }
    updateTag({ tagId: tag.id, payload: body });
  };

  const handleDelete = (tag: ResolutionStrategyTag): void => {
    if (!window.confirm(`¿Eliminar la categoría "${tag.name}"?`)) return;
    removeTag(tag.id);
  };

  const computeDropPosition = (e: React.DragEvent<HTMLLIElement>): DropPosition => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const pct = rect.height === 0 ? 0.5 : y / rect.height;
    if (pct < 0.25) return 'before';
    if (pct > 0.75) return 'after';
    // Middle band re-parents the dragged row under the target — even leaf
    // nodes are valid containers (they simply gain a child and stop being
    // leaves).
    return 'inside';
  };

  const isDropInvalid = (target: DropTarget): boolean => {
    if (draggingTagId == null) return true;
    if (target.id === draggingTagId) return true;
    return isDescendantOf(target.id, draggingTagId, tagsForStrategy);
  };

  const handleDragStart = (row: TagWithDepth): void => {
    setDraggingTagId(row.id);
    setEditingTagId(null);
  };

  const handleDragOver = (e: React.DragEvent<HTMLLIElement>, row: TagWithDepth): void => {
    if (draggingTagId == null) return;
    e.preventDefault();
    const next: DropTarget = {
      id: row.id,
      position: computeDropPosition(e),
    };
    setDropTarget((current) => {
      if (current && current.id === next.id && current.position === next.position) {
        return current;
      }
      return next;
    });
    // eslint-disable-next-line no-param-reassign
    e.dataTransfer.dropEffect = isDropInvalid(next) ? 'none' : 'move';
  };

  const handleDragLeave = (): void => {
    // Don't clear immediately — drag-over of a child element fires drag-leave
    // on the parent. We let the next drag-over re-set the target instead.
  };

  const handleDragEnd = (): void => {
    setDraggingTagId(null);
    setDropTarget(null);
  };

  const handleDrop = (): void => {
    if (draggingTagId == null || dropTarget == null || !selectedStrategyId) {
      handleDragEnd();
      return;
    }
    if (isDropInvalid(dropTarget)) {
      handleDragEnd();
      return;
    }
    const items = applyDrop(tagsForStrategy, draggingTagId, dropTarget);
    if (!items) {
      handleDragEnd();
      return;
    }
    // Optimistic UI: clear drag state immediately.
    handleDragEnd();
    reorderTags({ strategy_id: selectedStrategyId, items });
  };

  const expandAll = (): void => setCollapsedTagIds(new Set());
  const collapseAll = (): void => {
    // Collapse every node that has children.
    const next = new Set<number>();
    childCountById.forEach((_, parentId) => next.add(parentId));
    setCollapsedTagIds(next);
  };

  const parentOptionsForCreate = useMemo(() => flattenedTags, [flattenedTags]);

  const parentOptionsForEdit = (tag: ResolutionStrategyTag): TagWithDepth[] => {
    return flattenedTags.filter((opt) => !isDescendantOf(opt.id, tag.id, tagsForStrategy));
  };

  const renderRow = (row: TagWithDepth): React.ReactNode => {
    const dropPosition = dropTarget?.id === row.id ? dropTarget.position : null;
    const dragInvalid =
      dropPosition !== null ? isDropInvalid({ id: row.id, position: dropPosition }) : false;
    const childCount = childCountById.get(row.id) ?? 0;
    return (
      <TagRow
        key={row.id}
        row={row}
        parentOptions={parentOptionsForEdit(row)}
        isEditing={editingTagId === row.id}
        isCollapsed={collapsedTagIds.has(row.id)}
        hasChildren={childCount > 0}
        childCount={childCount}
        onToggleCollapse={() => toggleCollapse(row.id)}
        onStartEdit={() => setEditingTagId(row.id)}
        onCancelEdit={() => setEditingTagId(null)}
        onSave={(payload) => handleUpdate(row, payload)}
        onDelete={() => handleDelete(row)}
        onAddChild={() => {
          setCreateDefaultParentId(row.id);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        isSaving={isUpdating}
        isDeleting={isDeleting}
        onDragStart={() => handleDragStart(row)}
        onDragOver={(e) => handleDragOver(e, row)}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onDragEnd={handleDragEnd}
        isDragging={draggingTagId === row.id}
        isDragInvalid={dragInvalid}
        dropPosition={dropPosition}
      />
    );
  };

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Icon name={OrbitIcons.Workflow} size={16} className="text-blue-600" />
          <h3 className="text-sm font-semibold text-black-1.0">Tabulaciones / Sub-categorías</h3>
        </div>
        <div className="flex items-center gap-2">
          {strategies.length > 0 && (
            <select
              value={selectedStrategyId ?? ''}
              onChange={(e) =>
                setSelectedStrategyId(e.target.value === '' ? null : Number(e.target.value))
              }
              className="rounded border border-white-3.0 px-2 py-1 text-sm"
            >
              {strategies.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => setShowCreateStrategy(true)}
            className="flex items-center gap-1 rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white-0.0 hover:bg-blue-700"
          >
            <Icon name={OrbitIcons.PlusCircle} size={14} />
            Nueva estrategia
          </button>
        </div>
      </div>

      {isLoadingStrategies && (
        <div className="rounded-md border border-white-2.0 bg-white-0.0 p-4 text-sm text-gray-2.0">
          Cargando estrategias…
        </div>
      )}

      {strategiesError && (
        <div className="bg-red-50 p-4 text-sm text-red-700 border border-red-200 rounded-md">
          No se pudieron cargar las estrategias de resolución.
        </div>
      )}

      {!isLoadingStrategies && !strategiesError && strategies.length === 0 && !showCreateStrategy && (
        <div className="rounded-md border border-white-2.0 bg-white-0.0 p-4 text-center">
          <p className="text-sm text-gray-2.0 mb-3">
            La cuenta no tiene estrategias de resolución.
          </p>
          <button
            type="button"
            onClick={() => setShowCreateStrategy(true)}
            className="inline-flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white-0.0 hover:bg-blue-700"
          >
            <Icon name={OrbitIcons.PlusCircle} size={14} />
            Crear primera estrategia
          </button>
        </div>
      )}

      {showCreateStrategy && (
        <div className="bg-blue-50 p-4 border border-blue-200 rounded-md">
          <h4 className="mb-3 text-sm font-semibold text-black-1.0">
            Nueva estrategia de resolución
          </h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[180px]">
              <label
                htmlFor="new-strategy-name"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Nombre<span className="ml-0.5 text-red-500">*</span>
              </label>
              <input
                id="new-strategy-name"
                type="text"
                value={newStrategyName}
                onChange={(e) => setNewStrategyName(e.target.value)}
                placeholder="Ej: Estrategia principal"
                className="w-full rounded border border-white-3.0 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="min-w-[140px]">
              <label
                htmlFor="new-strategy-tag"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Categoría inicial
              </label>
              <input
                id="new-strategy-tag"
                type="text"
                value={newStrategyTag}
                onChange={(e) => setNewStrategyTag(e.target.value)}
                placeholder="Ej: Otros"
                className="w-full rounded border border-white-3.0 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="min-w-[120px]">
              <label
                htmlFor="new-strategy-resolution-time"
                className="mb-1 block text-xs font-medium text-gray-3.0"
              >
                Tiempo resolución (s)
              </label>
              <input
                id="new-strategy-resolution-time"
                type="number"
                value={newStrategyResolutionTime}
                onChange={(e) => {
                  const parsed = Number.parseInt(e.target.value, 10);
                  setNewStrategyResolutionTime(Number.isNaN(parsed) ? 0 : parsed);
                }}
                min={1}
                className="w-full rounded border border-white-3.0 px-2 py-1.5 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() =>
                  createStrategy({
                    name: newStrategyName.trim(),
                    tags: newStrategyTag.trim() ? [{ name: newStrategyTag.trim() }] : [],
                    resolution_time: newStrategyResolutionTime,
                  })
                }
                disabled={isCreatingStrategy || !isNewStrategyValid}
                className="rounded bg-blue-600 px-4 py-1.5 text-sm font-medium text-white-0.0 disabled:opacity-50 hover:bg-blue-700"
              >
                {isCreatingStrategy ? 'Creando...' : 'Crear'}
              </button>
              <button
                type="button"
                onClick={resetCreateStrategyForm}
                className="rounded border border-white-3.0 px-4 py-1.5 text-sm font-medium text-gray-3.0 hover:bg-white-1.0"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedStrategy && (
        <>
          <CreateTagForm
            parentOptions={parentOptionsForCreate}
            defaultParentId={createDefaultParentId}
            onSubmit={(payload) => {
              handleCreate(payload);
              setCreateDefaultParentId(null);
            }}
            isSaving={isCreating}
          />

          <div className="flex items-center justify-between text-xs text-gray-3.0">
            <span>
              {rootGroups.length} categoría{rootGroups.length === 1 ? '' : 's'} raíz ·{' '}
              {flattenedTags.length} en total
            </span>
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-gray-2.0">
                Arrastra una fila para reordenar o anidar
              </span>
              {childCountById.size > 0 && (
                <>
                  <button
                    type="button"
                    onClick={expandAll}
                    className="text-[11px] text-blue-600 hover:underline"
                  >
                    Expandir todo
                  </button>
                  <span className="text-gray-2.0">·</span>
                  <button
                    type="button"
                    onClick={collapseAll}
                    className="text-[11px] text-blue-600 hover:underline"
                  >
                    Colapsar todo
                  </button>
                </>
              )}
            </div>
          </div>

          {rootGroups.length === 0 ? (
            <div className="rounded-md border border-white-2.0 bg-white-0.0 px-3 py-4 text-sm text-gray-2.0">
              Aún no hay categorías. Agrega la primera arriba.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {rootGroups.map((group) => {
                const visibleSubtree = group.subtree.filter((row) => !isRowHidden(row));
                return (
                  <div
                    key={group.root.id}
                    className="overflow-hidden rounded-md border border-white-2.0 bg-white-0.0"
                  >
                    <ul className="p-0 m-0 list-none">{visibleSubtree.map(renderRow)}</ul>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
